import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useAuditLogs, type AuditLog } from "@/hooks/useAuditLogs";
import { format } from "date-fns";
import { FileText, RefreshCw, Filter } from "lucide-react";

const ENTITY_TYPES = [
  { value: "all", label: "All Types" },
  { value: "client", label: "Clients" },
  { value: "wine_lot", label: "Wine Lots" },
  { value: "vessel", label: "Vessels" },
];

const ACTION_COLORS = {
  INSERT: "bg-green-500/10 text-green-600 border-green-500/20",
  UPDATE: "bg-blue-500/10 text-blue-600 border-blue-500/20",
  DELETE: "bg-red-500/10 text-red-600 border-red-500/20",
};

export function AuditLogsTab() {
  const [entityType, setEntityType] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const { data: logs, isLoading, refetch } = useAuditLogs({
    entityType: entityType === "all" ? undefined : entityType,
    startDate: startDate ? new Date(startDate) : undefined,
    endDate: endDate ? new Date(endDate) : undefined,
  });

  const handleViewDetails = (log: AuditLog) => {
    setSelectedLog(log);
    setDetailsOpen(true);
  };

  const renderJsonDiff = (before: any, after: any) => {
    if (!before && !after) return null;

    const beforeKeys = before ? Object.keys(before) : [];
    const afterKeys = after ? Object.keys(after) : [];
    const allKeys = Array.from(new Set([...beforeKeys, ...afterKeys]));

    return (
      <div className="space-y-2">
        {allKeys.map((key) => {
          const beforeValue = before?.[key];
          const afterValue = after?.[key];
          const hasChanged = JSON.stringify(beforeValue) !== JSON.stringify(afterValue);

          if (!hasChanged && beforeValue === undefined) return null;

          return (
            <div key={key} className="rounded-lg border p-3 space-y-1">
              <p className="text-sm font-medium">{key}</p>
              {hasChanged && (
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Before:</span>
                    <pre className="mt-1 p-2 bg-red-500/5 rounded text-xs overflow-x-auto">
                      {JSON.stringify(beforeValue, null, 2)}
                    </pre>
                  </div>
                  <div>
                    <span className="text-muted-foreground">After:</span>
                    <pre className="mt-1 p-2 bg-green-500/5 rounded text-xs overflow-x-auto">
                      {JSON.stringify(afterValue, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
              {!hasChanged && (
                <pre className="text-xs p-2 bg-muted rounded overflow-x-auto">
                  {JSON.stringify(beforeValue || afterValue, null, 2)}
                </pre>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Audit Logs</CardTitle>
          <CardDescription>
            View all system activity and changes to critical data
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Filters */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <h3 className="text-sm font-medium">Filters</h3>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label>Entity Type</Label>
                <Select value={entityType} onValueChange={setEntityType}>
                  <SelectTrigger>
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent>
                    {ENTITY_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setEntityType("all");
                  setStartDate("");
                  setEndDate("");
                }}
              >
                Clear Filters
              </Button>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>

          <Separator />

          {/* Audit Log List */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium">
                Recent Activity ({logs?.length || 0})
              </h3>
            </div>

            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">
                Loading audit logs...
              </div>
            ) : logs && logs.length > 0 ? (
              <ScrollArea className="h-[500px] rounded-md border">
                <div className="p-4 space-y-2">
                  {logs.map((log) => (
                    <Card
                      key={log.id}
                      className="cursor-pointer hover:bg-accent transition-colors"
                      onClick={() => handleViewDetails(log)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 space-y-1">
                            <div className="flex items-center gap-2">
                              <Badge
                                variant="outline"
                                className={ACTION_COLORS[log.action as keyof typeof ACTION_COLORS]}
                              >
                                {log.action}
                              </Badge>
                              <Badge variant="outline">{log.entity_type}</Badge>
                              <span className="text-xs text-muted-foreground">
                                ID: {log.entity_id.slice(0, 8)}...
                              </span>
                            </div>

                            <p className="text-sm">
                              <span className="font-medium">
                                {log.actor?.display_name || "System"}
                              </span>
                              {" "}
                              <span className="text-muted-foreground">
                                {log.action === "INSERT" && "created"}
                                {log.action === "UPDATE" && "updated"}
                                {log.action === "DELETE" && "deleted"}
                              </span>
                              {" "}
                              <span className="font-medium">
                                {log.entity_type.replace(/_/g, " ")}
                              </span>
                            </p>

                            <p className="text-xs text-muted-foreground">
                              {format(new Date(log.at), "PPpp")}
                            </p>
                          </div>

                          <Button variant="ghost" size="sm">
                            <FileText className="h-4 w-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </ScrollArea>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No audit logs found</p>
                <p className="text-sm">Try adjusting your filters</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Audit Log Details</DialogTitle>
            <DialogDescription>
              Complete details of this audit log entry
            </DialogDescription>
          </DialogHeader>

          {selectedLog && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Action</p>
                  <Badge className={ACTION_COLORS[selectedLog.action as keyof typeof ACTION_COLORS]}>
                    {selectedLog.action}
                  </Badge>
                </div>
                <div>
                  <p className="text-muted-foreground">Entity Type</p>
                  <p className="font-medium">{selectedLog.entity_type}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Entity ID</p>
                  <p className="font-mono text-xs">{selectedLog.entity_id}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Timestamp</p>
                  <p className="font-medium">{format(new Date(selectedLog.at), "PPpp")}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Actor</p>
                  <p className="font-medium">
                    {selectedLog.actor?.display_name || "System"}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Email</p>
                  <p className="text-xs">{selectedLog.actor?.email || "N/A"}</p>
                </div>
              </div>

              <Separator />

              {/* Changes */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Changes</h4>
                {renderJsonDiff(selectedLog.before, selectedLog.after)}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
